import * as React from 'react'
import { Check, ChevronsUpDown, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'

interface InstitucionComboboxProps {
  /** Lista de instituciones existentes */
  instituciones: string[]
  /** Valor actual (puede ser una existente o nueva) */
  value: string | null
  /** Callback cuando cambia el valor */
  onValueChange: (value: string | null) => void
  /** Placeholder del input */
  placeholder?: string
  /** Si está deshabilitado */
  disabled?: boolean
  /** Clase CSS para el botón trigger */
  className?: string
  /** Si tiene error de validación */
  hasError?: boolean
}

export function InstitucionCombobox({
  instituciones,
  value,
  onValueChange,
  placeholder = 'Seleccionar o escribir institución...',
  disabled = false,
  className,
  hasError = false,
}: InstitucionComboboxProps) {
  const [open, setOpen] = React.useState(false)
  const [searchQuery, setSearchQuery] = React.useState('')

  // Filtrar instituciones por búsqueda
  const filteredInstituciones = React.useMemo(() => {
    if (!searchQuery) return instituciones

    const query = searchQuery.toLowerCase()
    return instituciones.filter((inst) =>
      inst.toLowerCase().includes(query)
    )
  }, [instituciones, searchQuery])

  // Verificar si el texto de búsqueda ya existe (case insensitive)
  const searchExistsInList = React.useMemo(() => {
    if (!searchQuery) return true
    return instituciones.some(
      (inst) => inst.toLowerCase() === searchQuery.toLowerCase()
    )
  }, [instituciones, searchQuery])

  // Manejar selección de una institución existente
  const handleSelect = (institucion: string) => {
    onValueChange(institucion)
    setOpen(false)
    setSearchQuery('')
  }

  // Manejar creación de nueva institución
  const handleCreate = () => {
    if (searchQuery.trim()) {
      onValueChange(searchQuery.trim())
      setOpen(false)
      setSearchQuery('')
    }
  }

  // Sincronizar searchQuery cuando se abre con un valor existente
  React.useEffect(() => {
    if (open && value) {
      setSearchQuery(value)
    }
  }, [open, value])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'w-full justify-between font-normal',
            !value && 'text-muted-foreground',
            hasError && 'border-destructive focus-visible:ring-destructive',
            className
          )}
        >
          <span className="truncate">
            {value || placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-(--radix-popover-trigger-width) p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Buscar o escribir..."
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <CommandList>
            {/* Lista de instituciones existentes */}
            {filteredInstituciones.length > 0 && (
              <CommandGroup>
                {filteredInstituciones.map((institucion) => (
                  <CommandItem
                    key={institucion}
                    value={institucion}
                    onSelect={() => handleSelect(institucion)}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        value === institucion ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    {institucion}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {/* Mensaje cuando no hay resultados */}
            {filteredInstituciones.length === 0 && !searchQuery && (
              <CommandEmpty>No hay instituciones registradas.</CommandEmpty>
            )}

            {/* Opción para agregar nueva institución */}
            {searchQuery.trim() && !searchExistsInList && (
              <>
                {filteredInstituciones.length > 0 && <CommandSeparator />}
                <CommandGroup>
                  <CommandItem
                    onSelect={handleCreate}
                    className="text-primary"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Agregar "{searchQuery.trim()}"
                  </CommandItem>
                </CommandGroup>
              </>
            )}

            {/* Cuando hay búsqueda pero no hay resultados similares */}
            {filteredInstituciones.length === 0 && searchQuery && searchExistsInList && (
              <CommandEmpty>
                Presiona Enter o selecciona para usar "{searchQuery}"
              </CommandEmpty>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
